// client/src/coms/views/chatview_parts/useChatDerivedMessages.js
import { useMemo } from 'react';
import {
  safeObj,
  safeArr,
  toInt,
  normalizeNameForCompare,
  formatStamp,
  avatarUrlForUserId,
  getMsFromCreatedAt,
} from './chatUtils';

function normalizeReactionEntry(entry) {
  const e = entry && typeof entry === 'object' ? entry : {};
  const users = safeArr(e.users).map((u) => {
    const user = u && typeof u === 'object' ? u : {};
    return {
      id: toInt(user.id, 0),
      username: String(user.username || '').trim(),
    };
  });

  return {
    emoji: String(e.emoji || '').trim(),
    count: Math.max(0, toInt(e.count, 0)),
    reacted: !!e.reacted,
    users,
  };
}

function normalizeReactionsArray(list) {
  return safeArr(list)
    .map((entry) => normalizeReactionEntry(entry))
    .filter((entry) => entry.emoji && entry.count > 0);
}

export default function useChatDerivedMessages({
  messagesRaw,
  membersNameMap,
  myChatLabel,
  myUserId,
  ownerUserId,
  messageSearch,
  canModerateMessage,
  isMessageEdited,
  isMessageDeleted,
}) {
  function authorLabelForMessage(msg) {
    const m = safeObj(msg);
    const msgId = m.user_id != null ? toInt(m.user_id, null) : null;

    // Messages the server posts itself (automations, safety alerts, event reminders) have no author.
    if (m.system_kind && !msgId) return m.system_kind === 'safety' ? 'Voxemera Safety' : m.system_kind === 'event' ? 'Events' : 'Automation';

    if (msgId != null) {
      const n = membersNameMap && membersNameMap[msgId] ? String(membersNameMap[msgId]).trim() : '';
      if (n) return n;
    }

    if (myUserId != null && msgId != null && Number(myUserId) === Number(msgId)) {
      return myChatLabel || String(m.username || '').trim() || 'You';
    }

    const fallback = String(m.username || '').trim();
    if (fallback) return fallback;
    if (msgId != null) return `User ${msgId}`;
    return 'User';
  }

  const messages = useMemo(() => {
    return safeArr(messagesRaw).map((m) => {
      const mm = safeObj(m);
      const author = authorLabelForMessage(mm);
      const stamp = formatStamp(mm.created_at);
      const edited = isMessageEdited(mm);
      const text = String(mm.body || '');
      const font = String(mm.font || '').trim() || 'inherit';
      const userId = mm.user_id != null ? toInt(mm.user_id, null) : null;
      const avatarUrl = avatarUrlForUserId(userId);
      const createdAtRaw = mm.created_at != null ? String(mm.created_at) : '';
      const createdAtMs = getMsFromCreatedAt(createdAtRaw);
      const reactions = normalizeReactionsArray(mm.reactions);

      return {
        id: mm.id != null ? String(mm.id) : `${Math.random()}`,
        raw: mm,
        author,
        stamp,
        text,
        font,
        userId,
        avatarUrl,
        edited,
        canModerate: canModerateMessage(mm),
        createdAtRaw,
        createdAtMs,
        reactions,
        systemKind: mm.system_kind ? String(mm.system_kind) : '',
      };
    });
  }, [
    messagesRaw,
    membersNameMap,
    myChatLabel,
    myUserId,
    ownerUserId,
    canModerateMessage,
    isMessageEdited,
  ]);

  const messageSearchNorm = useMemo(() => String(messageSearch || '').trim().toLowerCase(), [messageSearch]);

  const filteredMessages = useMemo(() => {
    if (!messageSearchNorm) return messages;
    return messages.filter((m) => {
      const author = String(m.author || '').toLowerCase();
      const text = String(m.text || '').toLowerCase();
      return author.includes(messageSearchNorm) || text.includes(messageSearchNorm);
    });
  }, [messages, messageSearchNorm]);

  const messagesWithGroupMeta = useMemo(() => {
    const maxGapMs = 60 * 1000;

    return filteredMessages.map((m, i) => {
      const prev = i > 0 ? filteredMessages[i - 1] : null;

      let groupedWithPrev = false;
      if (prev) {
        const sameUser =
          m.userId != null && prev.userId != null
            ? Number(m.userId) === Number(prev.userId)
            : normalizeNameForCompare(m.author) === normalizeNameForCompare(prev.author);

        const gapOk =
          Number.isFinite(m.createdAtMs) &&
          Number.isFinite(prev.createdAtMs) &&
          m.createdAtMs >= prev.createdAtMs &&
          m.createdAtMs - prev.createdAtMs <= maxGapMs;

        // System notices (announcements, encryption changes...) always start their own group, so they never read
        // as part of someone's message.
        groupedWithPrev =
          sameUser &&
          gapOk &&
          !m.systemKind &&
          !prev.systemKind &&
          !isMessageDeleted(m.raw) &&
          !isMessageDeleted(prev.raw);
      }

      return {
        ...m,
        groupedWithPrev,
      };
    });
  }, [filteredMessages, isMessageDeleted]);

  return {
    messages,
    messageSearchNorm,
    filteredMessages,
    messagesWithGroupMeta,
  };
}